/**
 * Alunos e convites, na visão do profissional.
 *
 * **O token aparece uma vez.** Depois de gerado, ele fica na tela até a pessoa
 * sair dela — e não volta. O botão de compartilhar usa o compartilhamento
 * nativo do aparelho quando existe (SPEC §6.1), com cópia para a área de
 * transferência como alternativa.
 *
 * Não há botão de "ver link de novo", e é decisão: para existir, o servidor
 * teria de guardar o token de forma recuperável, e aí quem tivesse acesso ao
 * banco poderia aceitar convite no lugar de qualquer aluno.
 */

import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { Alert } from '@/components/Alert';
import { invitationLink, type CreatedInvitation } from '@/features/relationships/api';
import {
  relationshipErrorKey,
  useCreateInvitation,
  useInvitations,
  useRevokeInvitation,
  useStudents,
} from '@/features/relationships/useRelationships';

const LIVE: ReadonlyArray<string> = ['pending', 'active', 'paused'];

export default function StudentsPage() {
  const { t, i18n } = useTranslation(['relationships', 'common']);
  const students = useStudents();
  const invitations = useInvitations();
  const create = useCreateInvitation();
  const revoke = useRevokeInvitation();

  const [hint, setHint] = useState('');
  const [fresh, setFresh] = useState<CreatedInvitation | null>(null);
  const [copied, setCopied] = useState(false);
  const [copyFailed, setCopyFailed] = useState(false);
  // O convite gerado a partir de um encerrado aparece no bloco lá de cima. Sem
  // levar a tela até ele, o profissional toca em "Convidar de novo" e parece
  // que nada aconteceu.
  const conviteRef = useRef<HTMLElement>(null);

  const dates = new Intl.DateTimeFormat(i18n.language, { dateStyle: 'medium' });

  // `pending` fica com os ativos: é convite aceito por ninguém ainda, e o lugar
  // dele é onde o profissional está olhando, não no histórico.
  const todos = students.data ?? [];
  const ativos = todos.filter((link) => LIVE.includes(link.status));
  const encerrados = todos.filter((link) => !LIVE.includes(link.status));

  /**
   * Envia o link, e só diz que copiou quando copiou mesmo.
   *
   * A primeira versão anunciava "Link copiado" em três situações em que nada
   * tinha sido copiado: navegador sem Clipboard API (o `?.` devolvia
   * `undefined` e o código seguia em frente), escrita recusada por permissão, e
   * compartilhamento cancelado pela própria pessoa. O profissional colava no
   * WhatsApp e mandava o que estivesse na área de transferência de antes.
   */
  async function share(token: string) {
    const url = invitationLink(token);
    setCopied(false);
    setCopyFailed(false);

    if (navigator.share) {
      try {
        await navigator.share({ title: t('relationships:invite.shareTitle'), url });
        return;
      } catch (error) {
        // Cancelar não é falhar: quem desistiu não quer o link na área de
        // transferência nem uma mensagem dizendo que foi copiado.
        if (error instanceof DOMException && error.name === 'AbortError') return;
      }
    }

    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
    } catch {
      // Inclui o caso de `navigator.clipboard` inexistente, que levanta
      // `TypeError` ao acessar `writeText`. O link continua na tela para
      // seleção manual — que é o que a mensagem passa a dizer.
      setCopyFailed(true);
    }
  }

  return (
    <div className="flex flex-col gap-6 px-5 py-6">
      <header>
        <h1 className="text-3xl">{t('relationships:students.title')}</h1>
        <p className="mt-1 text-(--color-sand-700)">{t('relationships:students.subtitle')}</p>
      </header>

      <section className="mh-card" ref={conviteRef}>
        <h2 className="text-sm font-semibold text-(--color-sand-900)">
          {t('relationships:invite.newTitle')}
        </h2>

        <div className="mh-field mt-3">
          <label htmlFor="hint">{t('relationships:invite.hintLabel')}</label>
          <p className="mb-2 text-sm text-(--color-sand-700)">
            {t('relationships:invite.hintHelp')}
          </p>
          <input
            id="hint"
            type="text"
            maxLength={80}
            value={hint}
            onChange={(event) => setHint(event.target.value)}
          />
        </div>

        <button
          type="button"
          className="mh-button mh-button--primary mt-4 w-full"
          disabled={create.isPending}
          onClick={() => {
            setCopied(false);
            create.mutate(hint.trim() || null, {
              onSuccess: (created) => {
                setFresh(created);
                setHint('');
              },
            });
          }}
        >
          {create.isPending ? t('common:state.loading') : t('relationships:invite.generate')}
        </button>

        {create.isError ? (
          <div className="mt-3">
            <Alert tone="error" live="assertive">
              {t(relationshipErrorKey(create.error))}
            </Alert>
          </div>
        ) : null}

        {fresh ? (
          <div className="mt-4 rounded-xl bg-(--color-amber-50) p-4">
            <p className="text-sm font-semibold text-(--color-sand-900)">
              {t('relationships:invite.readyTitle')}
            </p>
            <p className="mt-1 text-sm text-(--color-sand-700)">
              {t('relationships:invite.readyOnce')}
            </p>
            {/* O link fica visível para quem preferir copiar à mão, e quebra
                em qualquer ponto para não estourar a largura no celular. */}
            <p className="mt-3 break-all text-sm text-(--color-sand-900)">
              {invitationLink(fresh.token)}
            </p>
            <button
              type="button"
              className="mh-button mh-button--secondary mt-3 w-full"
              onClick={() => void share(fresh.token)}
            >
              {t('relationships:invite.share')}
            </button>
            {copied ? (
              <p className="mt-2 text-center text-sm text-(--color-mint-600)">
                {t('relationships:invite.copied')}
              </p>
            ) : null}
            {copyFailed ? (
              <p className="mt-2 text-center text-sm text-(--color-sand-700)">
                {t('relationships:invite.copyFailed')}
              </p>
            ) : null}
          </div>
        ) : null}
      </section>

      {invitations.data && invitations.data.length > 0 ? (
        <section>
          <h2 className="mb-2 text-sm font-semibold uppercase tracking-wide text-(--color-sand-500)">
            {t('relationships:invite.openTitle')}
          </h2>
          <ul className="flex flex-col gap-2">
            {invitations.data.map((item) => (
              <li key={item.invitation_id} className="mh-card flex items-center justify-between">
                <span>
                  <span className="block font-medium text-(--color-sand-900)">
                    {item.invitee_hint ?? t('relationships:invite.noHint')}
                  </span>
                  <span className="block text-sm text-(--color-sand-700)" data-numeric>
                    {t('relationships:invite.expires', {
                      date: dates.format(new Date(item.expires_at)),
                    })}
                  </span>
                </span>
                <button
                  type="button"
                  className="mh-button mh-button--ghost"
                  onClick={() => revoke.mutate(item.invitation_id)}
                >
                  {t('relationships:invite.revoke')}
                </button>
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      <section>
        <h2 className="mb-2 text-sm font-semibold uppercase tracking-wide text-(--color-sand-500)">
          {t('relationships:students.listTitle')}
        </h2>

        {students.isError ? <Alert tone="error">{t('relationships:error.list')}</Alert> : null}

        {ativos.length === 0 && !students.isPending ? (
          <p className="text-(--color-sand-700)">{t('relationships:students.empty')}</p>
        ) : null}

        <ul className="flex flex-col gap-2">
          {ativos.map((link) => (
            <li key={link.relationship_id} className="mh-card">
              <Link to={`/students/${link.relationship_id}`} className="block">
                <span className="block font-medium text-(--color-sand-900)">
                  {link.counterpart_name}
                </span>
                <span className="block text-sm text-(--color-sand-700)">
                  {t(`relationships:status.${link.status}`)}
                </span>
              </Link>
            </li>
          ))}
        </ul>
      </section>

      {/* Os encerrados em seção própria.

          Ficavam misturados com os ativos, e como o nome deixa de vir junto com
          o fim do vínculo, apareciam todos com o mesmo rótulo genérico. Aqui
          eles saem do caminho de quem está trabalhando e passam a ser
          identificados pelo apelido que o próprio profissional deu ao convite. */}
      {encerrados.length > 0 ? (
        <section>
          <h2 className="mb-2 text-sm font-semibold uppercase tracking-wide text-(--color-sand-500)">
            {t('relationships:students.endedTitle')}
          </h2>

          <ul className="flex flex-col gap-2">
            {encerrados.map((link) => (
              <li key={link.relationship_id} className="mh-card flex items-center justify-between">
                <span>
                  <span className="block font-medium text-(--color-sand-900)">
                    {link.invitee_hint ?? t('relationships:students.former')}
                  </span>
                  <span className="block text-sm text-(--color-sand-700)">
                    {t(`relationships:status.${link.status}`)}
                    {link.ended_at ? ` · ${dates.format(new Date(link.ended_at))}` : null}
                  </span>
                </span>
                {/* Retomar é convite novo, não um botão que reativa.

                    Quem encerrou pode ter sido o aluno, e reativar por decisão
                    do profissional devolveria a ele o acesso aos dados de saúde
                    sem que a pessoa dissesse nada. O convite recoloca o
                    consentimento no caminho. */}
                <button
                  type="button"
                  className="mh-button mh-button--ghost"
                  disabled={create.isPending}
                  onClick={() => {
                    setCopied(false);
                    setCopyFailed(false);
                    create.mutate(link.invitee_hint, {
                      onSuccess: (created) => {
                        setFresh(created);
                        // `?.` também no método: onde ele não existe, o link
                        // continua sendo gerado e exibido — rolar a tela é
                        // conveniência, e conveniência não derruba a operação.
                        conviteRef.current?.scrollIntoView?.({
                          behavior: 'smooth',
                          block: 'start',
                        });
                      },
                    });
                  }}
                >
                  {t('relationships:students.inviteAgain')}
                </button>
              </li>
            ))}
          </ul>
        </section>
      ) : null}
    </div>
  );
}
